/**
 * PM Entity System (epic gh#1867) PR-4 — the `pm_type_def.card_layout` CONTRACT
 * and its compiler.
 *
 * ── WHY THIS LIVES IN `@elmes/shared` ────────────────────────────────────────
 * The layout document is AUTHORED on the API side (the boot seed writes it, an
 * admin edits it from v1.5) and CONSUMED on the web side (the card renderer).
 * Both halves have to agree on exactly one question — "which field names may a
 * card name, and what does each resolve to on an item?" — so the answer is
 * derived ONCE here rather than twice, in two languages of the same repo.
 *
 * ── WHAT THIS IS NOT ─────────────────────────────────────────────────────────
 * NOT a window/compositor spec. PR-4's locked decision D4 is a DEDICATED,
 * MINIMAL card renderer: the kernel, the compositor and `WindowSpecRenderer` are
 * untouched. `WindowSpec` is an open `z.record` by design (it carries arbitrary
 * component props); `card_layout` is the OPPOSITE posture — a closed,
 * `.strict()` document whose whole content is an ordered list of field names
 * drawn from a closed vocabulary. Reusing the window schema here would have
 * imported an open bag into a surface whose only value is that it is closed.
 *
 * ── THE THREE FAIL-LOUD RULES ────────────────────────────────────────────────
 *   1. UNKNOWN KEY in the document      → reject (`.strict()`).
 *   2. UNKNOWN FIELD in `fields`        → THROW `PmCardLayoutError`.
 *   3. AMBIGUOUS FIELD (a name that is both a core field and a declared `data`
 *      key)                             → THROW `PmCardLayoutError`.
 * None of the three is a silent skip or a silent filter. A renderer that drops
 * the field it cannot resolve produces a card that looks CORRECT and is missing
 * data — the reader has no way to tell "this item has no owner" from "this
 * layout named a field that does not exist". The whole point of a closed
 * vocabulary is that the mismatch is visible; a silent filter throws that away
 * ([[feedback_closed_vocabulary_read_seam]] — strictness belongs where the
 * document is INTERPRETED, not only where it is written).
 *
 * ── K16: `data_schema` NULL ≠ `{}` ───────────────────────────────────────────
 * The column's NULL-vs-`{}` distinction is load-bearing and is NOT collapsed
 * here. `null` means "no contract authored": there is nothing to read, so this
 * module never parses anything. An OBJECT means "a contract was authored": it is
 * READ, and a `fields` bag that is not an object is a THROW rather than a shrug.
 *
 * WHERE THE DIFFERENCE IS ACTUALLY OBSERVABLE, stated precisely because the
 * obvious answer is wrong: NOT in the vocabulary. Both states declare zero data
 * keys, so both leave the card at the core set — which means a `dataSchema ?? {}`
 * coalesce produces the SAME compiled output and is invisible to any test that
 * only checks what compiles. (Measured: the coalesce survived the first version
 * of this module's test suite.) It shows up in the FAULT MESSAGE, because the
 * two states have DIFFERENT FIXES — see `PmDataVocabulary` below, and the
 * `THE DISTINGUISHING CASE` test that pins both directions.
 *
 * ── THE CASING BRIDGE (why a compiled field carries an accessor) ─────────────
 * `card_layout.fields` names `pm_item` COLUMNS — `owner_agent`, `owner_human` —
 * because that is what the shipped seed wrote into an `ON CONFLICT DO NOTHING`
 * row that no later boot can rewrite. The WIRE shape of an item is drizzle's
 * property projection, which is camelCase (`ownerAgent`, `ownerHuman`). So the
 * two are NOT the same string, and a renderer indexing an item by the layout's
 * word would read `undefined` for both owner fields and render two silently
 * blank rows. The compiler therefore returns an ACCESSOR per field, and that
 * snake→camel bridge exists in exactly one place
 * ([[feedback_wire_casing_projection_parity]]).
 */
import { z } from 'zod';
/**
 * Upper bound on the number of fields one card may carry. A card is a GLANCE
 * surface; twenty rows is already past the point where it stops being one, and
 * an unbounded array is an unbounded render.
 */
export const PM_CARD_LAYOUT_MAX_FIELDS = 20;
/**
 * The `pm_type_def.card_layout` document.
 *
 * `.strict()` is the load-bearing modifier: an unknown key is a REJECT, not a
 * silently-dropped field. `card_layout` is admin-editable from v1.5 and is a
 * plausible target for an LLM-authored patch, so a permissive object schema here
 * would be an open channel for keys the renderer has never heard of to sit in
 * the row looking effective.
 */
export const pmCardLayoutSchema = z
    .object({
    fields: z.array(z.string().min(1)).max(PM_CARD_LAYOUT_MAX_FIELDS),
})
    .strict();
/**
 * The CORE card vocabulary — the `pm_item` columns a card may show in v1.
 *
 * Spelled as the COLUMN names because that is the word the shipped seed wrote
 * (`pmIssueTypeSeed.ts`'s `ISSUE_CARD_LAYOUT`) into a row that `ON CONFLICT DO
 * NOTHING` makes un-rewritable. The two lists are deliberately NOT derived from
 * one another: this one is the RENDERER'S VOCABULARY (what a card may name) and
 * that one is a STORED DOCUMENT (what one shipped type happens to show). Folding
 * them would mean widening the vocabulary silently rewrites the seed, which is a
 * different and much worse thing than the duplication it removes. They are held
 * together by a PARITY TEST instead — `pmIssueTypeSeed.conformance.test.ts`
 * compiles the REAL seed constant against this REAL vocabulary, so the shipped
 * type is proven renderable rather than assumed to be.
 *
 * WHAT IS DELIBERATELY ABSENT IS COUNTED ELSEWHERE, BY MACHINE, and this paragraph
 * deliberately carries NO arithmetic of its own. It used to: it enumerated
 * `pm_item`'s columns, named the ones this list omits and closed with "5 + 9 = 14 is
 * what keeps that sentence from quietly becoming false the next time a column
 * lands". It did not keep it — gh#1966 WAS that sentence going stale (it had
 * silently lost `sort_order`), and prose cannot count itself
 * ([[feedback_prose_enumeration_needs_machine_count]]). Restating the sum with
 * today's numbers would only re-arm the same failure, so the claim lives where a
 * machine performs it: `PM_ITEM_CARD_ABSENT_COLUMNS` (`@elmes/api`'s
 * `kernel/itemCore.ts`) carries the other half of the partition, and
 * `pmIssueTypeSeed.conformance.test.ts` asserts the two lists are DISJOINT and UNION
 * to exactly `getTableColumns(pmItem)`. A column added tomorrow lands in one list or
 * turns that test red.
 *
 * It cannot be counted HERE for the reason the vocabulary cannot be derived from the
 * seed either: `@elmes/shared` cannot import `@elmes/api`, so this module has no way
 * to name `pm_item` at all.
 *
 * What remains a matter of judgement rather than arithmetic: every ADDITION to this
 * list is a decision. v1 was the epic's "minimal card"; `item_no` (gh#2313) is the
 * first widening since, and it did not arrive alone — widening this list changes only
 * what a FRESH tenant is seeded with, so it ships with the migration that reaches
 * already-seeded rows (`0041_pm_card_layout_item_no.sql`).
 */
export const BASE_CARD_FIELDS = [
    'title',
    'status',
    'priority',
    'owner_agent',
    'owner_human',
    // gh#2313 (SRS §9.2) — the ISSUE NUMBER, and the first name added to this list
    // since v1. It is card CONTENT in a way `id` is not: `id` is the surrogate every FK
    // and edge joins on and is deliberately absent above, while `item_no` is the
    // per-project handle a human quotes ("#12"), unique under
    // `uq_pm_item_tenant_project_item_no` and on the wire since gh#2274 dilim-1.
    //
    // gh#2274 recorded it as a DEFERRAL rather than a refusal (see
    // `PM_ITEM_CARD_ABSENT_COLUMNS`' history): the column existed and was renderable,
    // but this list is not a rendering hint — it is what the shipped `card_layout`
    // documents are written from, and `dmTypePackageSeed`'s INSERT is `ON CONFLICT DO
    // NOTHING`, so widening it alone would change the card for tenants seeded AFTER the
    // deploy and leave every existing tenant on the old two. That drift is what the
    // deferral was avoiding, and it is closed HERE by the backfill migration landing in
    // the same PR rather than by keeping the word out of the vocabulary.
    'item_no',
];
/**
 * CORE field name → the key it is read from on a wire item.
 *
 * The snake→camel bridge described in the module header, as DATA rather than as
 * a transformation: a mechanical `snakeToCamel()` would also "work" today and
 * would silently invent an accessor for any future column whose wire name is not
 * its mechanical camelisation. An explicit table cannot do that — a new core
 * field that is not listed here fails to compile, which is the correct moment to
 * make the decision.
 *
 * `as const satisfies …` RATHER THAN A `Record<…, string>` ANNOTATION, and the
 * difference is load-bearing rather than stylistic:
 *   - `satisfies` keeps the EXHAUSTIVENESS check (a new `PmBaseCardField` with no
 *     entry here is a compile error, exactly as the annotation gave);
 *   - `as const` keeps the VALUES as literal types instead of widening them to
 *     `string`, so the emitted `.d.ts` says `owner_agent: 'ownerAgent'`.
 * The second half is what makes the accessors checkable at all from outside this
 * package: `@elmes/api`'s `_assertCardAccessorParity` assigns this table to
 * `Record<PmBaseCardField, keyof PmItemRow>`, which under the old annotation was
 * `string` vs a literal union — an assignment that could only ever fail, i.e. a
 * pin that had to be deleted rather than one that could catch a typo. With the
 * literals preserved it passes normally and goes red on exactly the mistake it
 * exists for (`owner_agent: 'ownerAgnt'`).
 */
export const BASE_CARD_FIELD_ITEM_KEYS = {
    title: 'title',
    status: 'status',
    priority: 'priority',
    owner_agent: 'ownerAgent',
    owner_human: 'ownerHuman',
    // gh#2313 — the THIRD entry where the layout word and the wire key differ, and the
    // first one added since the table was written. `item_no` → `itemNo` is exactly the
    // silent-blank-row case the explicit table exists for: a mechanical camelisation
    // would also produce `itemNo` today, which is precisely why the omission would have
    // been invisible until a column whose wire name is not its mechanical camelisation
    // arrived.
    item_no: 'itemNo',
};
/**
 * The renderer's read of `pm_type_def.data_schema` — `fields`' KEYS and nothing
 * else.
 *
 * TOLERANT of keys it does not own (`additionalFields`, and whatever a later PR
 * adds) because this is a READ seam, not the write seam: the API's
 * `pmTypeDataSchemaSchema` is the authority on that document's full shape, and a
 * second `.strict()` copy here would reject valid contracts the moment the two
 * drifted. What it is NOT tolerant of is `fields` being present and not an
 * object — that is the part this module actually consumes, and a malformed
 * version of it must be loud rather than read as "no data fields".
 */
const cardDataSchemaVocabularySchema = z
    .object({
    fields: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
/**
 * A `card_layout` that cannot be compiled: a malformed document, a field outside
 * the vocabulary, or an ambiguous one.
 *
 * Its own class so the UI can tell "the layout is wrong" (an authoring fault the
 * operator must fix, shown as a visible error card) from "the fetch failed" and
 * from "you are not permitted" — three different things a caller must not have
 * to distinguish by string-matching a message.
 */
export class PmCardLayoutError extends Error {
    /** The offending field names, when the fault is per-field. */
    fields;
    constructor(message, fields = []) {
        super(message);
        this.name = 'PmCardLayoutError';
        this.fields = fields;
    }
}
/** The operator-facing half of the message above. */
function describeDataVocabulary(vocabulary) {
    if (!vocabulary.authored) {
        return 'This type has NO authored data_schema (SQL NULL — K16 "not authored yet"), so only the core fields are nameable; author a data_schema to declare data fields.';
    }
    if (vocabulary.keys.size === 0) {
        return 'The type HAS an authored data_schema and it declares no fields; add the field to data_schema.fields.';
    }
    return `Declared data fields: ${[...vocabulary.keys].sort().join(' / ')}.`;
}
/**
 * Compile a `card_layout` against its type's `data_schema`.
 *
 * @param layout     the raw `pm_type_def.card_layout` value.
 * @param dataSchema the raw `pm_type_def.data_schema` value — `null`/`undefined`
 *                   for K16's "not authored", anything else for an authored
 *                   contract. Typed `unknown` rather than `PmWireDataSchema`
 *                   ON PURPOSE: a validator must accept what a row actually
 *                   holds, including the malformed values it exists to reject.
 *                   Narrowing the parameter would push "is this even a value?"
 *                   onto every caller and make the guard trust its input.
 *
 * @throws {PmCardLayoutError} on a malformed document, an unknown field, a
 *         duplicate field, or a name that is ambiguous between the two sources.
 *
 * PURE: no I/O, no ambient state, no logging. It is called from an API
 * controller, a React render and a unit test alike, and each must be able to
 * treat the THROW as the whole story.
 */
export function compileCardLayout(layout, dataSchema) {
    const parsedLayout = pmCardLayoutSchema.safeParse(layout);
    if (!parsedLayout.success) {
        throw new PmCardLayoutError(`pm: card_layout is invalid — ${parsedLayout.error.issues
            .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
            .join('; ')}`);
    }
    const dataVocabulary = readDataSchemaVocabulary(dataSchema);
    const dataFieldKeys = dataVocabulary.keys;
    const compiled = [];
    const seen = new Set();
    const unknown = [];
    const ambiguous = [];
    const duplicated = [];
    for (const field of parsedLayout.data.fields) {
        if (seen.has(field)) {
            duplicated.push(field);
            continue;
        }
        seen.add(field);
        const isCore = Object.prototype.hasOwnProperty.call(BASE_CARD_FIELD_ITEM_KEYS, field);
        const isData = dataFieldKeys.has(field);
        // AMBIGUITY IS CHECKED BEFORE EITHER ARM WINS. Silently preferring the core
        // column would be deterministic and still wrong: the admin who declared a
        // `data` field under a core field's name gets a card showing the OTHER
        // value, with nothing anywhere saying so. Refusing costs them one rename and
        // tells them exactly what to rename.
        if (isCore && isData) {
            ambiguous.push(field);
            continue;
        }
        if (isCore) {
            compiled.push({
                field,
                source: 'core',
                key: BASE_CARD_FIELD_ITEM_KEYS[field],
            });
            continue;
        }
        if (isData) {
            compiled.push({ field, source: 'data', key: field });
            continue;
        }
        unknown.push(field);
    }
    // Reported per CLASS, each listing every offender, so one compile tells the
    // author everything that is wrong instead of one fault per fix-and-retry
    // round.
    const faults = [];
    if (unknown.length > 0) {
        faults.push(`unknown field(s): ${unknown.join(', ')} — a card field must be one of the core ` +
            `fields (${BASE_CARD_FIELDS.join(' / ')}) or a key declared in the type's ` +
            `data_schema.fields. ${describeDataVocabulary(dataVocabulary)}`);
    }
    if (ambiguous.length > 0) {
        faults.push(`ambiguous field(s): ${ambiguous.join(', ')} — the name is BOTH a core field and a ` +
            'declared data_schema key, so which value the card would show is undefined. ' +
            'Rename the data_schema key.');
    }
    if (duplicated.length > 0) {
        faults.push(`duplicate field(s): ${duplicated.join(', ')} — a card renders each field once; ` +
            'a repeat is an authoring mistake, not a layout.');
    }
    if (faults.length > 0) {
        throw new PmCardLayoutError(`pm: card_layout cannot be compiled — ${faults.join(' | ')}`, [...unknown, ...ambiguous, ...duplicated]);
    }
    return { fields: compiled };
}
/**
 * The `data`-side half of the vocabulary, WITH the authoring state that produced
 * it.
 *
 * `authored` is carried rather than inferred from `keys.size`, because the two
 * are not the same question: an unauthored contract and an authored-but-empty
 * one both declare ZERO keys. See `PmDataVocabulary` for why the difference is
 * kept.
 */
function readDataSchemaVocabulary(dataSchema) {
    // K16 "not authored yet" — there is no contract to read, so the vocabulary is
    // the core set. Note this is the ONLY branch that skips the parse: an authored
    // `{}` goes through it and means "authored, declaring no data fields".
    if (dataSchema === null || dataSchema === undefined) {
        return { authored: false, keys: new Set() };
    }
    const parsed = cardDataSchemaVocabularySchema.safeParse(dataSchema);
    if (!parsed.success) {
        throw new PmCardLayoutError(`pm: type_def.data_schema is malformed — ${parsed.error.issues
            .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
            .join('; ')}`);
    }
    return { authored: true, keys: new Set(Object.keys(parsed.data.fields ?? {})) };
}
/**
 * Read one compiled field off a wire item. The single place the `core` / `data`
 * split is applied, so the renderer cannot get it half-right.
 *
 * Returns `unknown` — formatting is the RENDERER's business, and a resolver that
 * pre-stringified would make "absent" and "the string 'null'" the same value.
 */
export function readPmCardField(item, field) {
    if (field.source === 'core')
        return item[field.key];
    const bag = item.data;
    if (bag === null || typeof bag !== 'object' || Array.isArray(bag))
        return undefined;
    return bag[field.key];
}
//# sourceMappingURL=pmCardLayout.js.map